import React, { useCallback, useEffect, useState } from 'react';
// import { Color, ColorPicker } from "material-ui-color";
import { Switch, FormControlLabel, Tooltip, TypographyTypeMap, Box } from '@mui/material';
import { useUserContext } from '../../../contexts/UserContext/use';
import { Typography } from 'antd';
import { useAttributePath } from './usePreferenceControls';
import { fnDebounceFunc } from '../../../services/shared';
import { AdminContextProps } from '../../../types/admin';
const { Text } = Typography;
// import { Variant } from '@mui/material/styles/createTypography';

// Registry pattern: app registers context hooks at startup
const contextHooksMap: Record<string, () => AdminContextProps> = {
  user: useUserContext,
};

export const registerContextHooks = (hooks: Record<string, () => AdminContextProps>) => {
  Object.assign(contextHooksMap, hooks);
};

type ObjectTypes =  "user" | 
                    "bugattiDeviceStatus" | "bugattiDeviceSettings" | 
                    "kodiakServiceStatus" | "kodiakServiceSettings" | "kodiakEndpointStatus" | "kodiakEndpointSettings" | 
                    "systemServiceSettings" | "systemServiceStatus" |
                    "trendMetricAttributes" |
                    "sputnikDeviceAttributes"|
                    "nebulonTileAttributes" | "nebulonTileSputnikDeviceStatus" |
                    "nebulonRoomAttributes" |
                    "javelintaskAttributes" |
                    "cortexClimateSettings";
export interface PreferenceProps {
  attributePath?: string;
  objectType: ObjectTypes;
  defaultValue?: any;
  disabled?: boolean
  ttt?:string
  readOnly?:Boolean
  onlyIfKeyExists?:Boolean;
  unlimitedValue?: any;
  debounceDelay?:number;
  onValueChange?: (newValue: any) => void;
  valueFormatter?: (sourceValue: any,bWriteBack: boolean) => any;
}


interface GetLabelStyleProps {
  disabled: boolean;
  labelVariant?: string;
}

export const getLabelStyle = ({ disabled, labelVariant }: GetLabelStyleProps): React.CSSProperties => {
  if (disabled) {
    return { color: "rgba(0, 0, 0, 0.38)" };
  }

  switch (labelVariant) {
    case "subtitle2":    
      return {};
    case "caption":
    default:
        return {
          margin: "0.35em 0px 0px ",
          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
          fontWeight: 400,
          fontSize: "0.75rem",
          lineHeight: 1.66,
          letterSpacing: "0.03333em",
          display: "block"
      };

  }
};

/**
 * A React hook to retrieve and update an object's attributes dynamically.
 *
 * @param {ObjectTypes} objectType - The type of object to fetch attributes from.
 * @param {string} [attributePath] - The path to the attribute within the object.
 * @param {any} [defaultValue] - The default value to use if the attribute is missing.
 * @param {(value: any, bWriteBack: boolean) => any} [valueFormatter] - 
 *        A function to transform the value before returning it, or convert it back before writing.
 *        - If `bWriteBack` is `false`, it processes the retrieved value.
 *        - If `bWriteBack` is `true`, it converts the value before updating.
 *
 * @returns {{
*   value: any;
*   updateAttribute: (newValue: any) => void;
*   keyExists: boolean;
* }} - An object containing:
*        - `value`: The processed attribute value.
*        - `updateAttribute`: A function to update the attribute.
*        - `keyExists`: A boolean indicating if the attribute exists.
*
* @example
* // Example usage with a boolean value stored as a string in the backend:
* const { value, updateAttribute } = useObjectAttributes(
*   "nebulonTileSputnikDeviceStatus",
*   `dps.${plugId+1}`,
*   false, 
*   (value, writeBack) => writeBack ? (value ? "100" : "0") : stringToBoolean(value, { format: 'bool' })
* );
*
* console.log(value); // true or false
* updateAttribute(true); // Updates backend value to "100"
*/
export const useObjectAttributes = (objectType: ObjectTypes, attributePath?: string, defaultValue?: any, valueFormatter?: (value:any,bWriteBack:boolean)=> any) => {
    // Handle null or missing attributePath
    if (!attributePath) {
      if (valueFormatter) {defaultValue = valueFormatter(defaultValue,false)}
      return {
        value: defaultValue || null,
        updateAttribute: () => {},
        keyExists: false
      };
    }

  const contextHook = contextHooksMap[objectType];

  if (!contextHook) {
      throw new Error(`Invalid objectType: ${objectType}`);
  }

  const { getObjectAttribute, getObjectAttributeExists, updateObjectAttribute } = contextHook();
  const { value, updateAttribute, keyExists } = useAttributePath({ 
    attributePath, 
    getObjectAttribute, 
    updateObjectAttribute,
    defaultValue,
    getObjectAttributeExists
  });
  let newValue = value
  if (valueFormatter) {
    newValue = valueFormatter(value,false)
  }
  
  return {
    value: newValue,
    updateAttribute,
    keyExists
  };

};

interface WrappedControlProps {
  unlimitedValue?: any;
  defaultValue?: any;
  attributePath?: string;
  label: React.ReactNode;
  objectType: ObjectTypes;
  value: any;
  ttt?: any;
  [key: string]: any;
}

export const WrappedControl: React.FC<WrappedControlProps> = ({
  children,
  unlimitedValue,
  defaultValue,
  attributePath,
  label,
  objectType,
  value,
  ttt,
  ...restProps
}) => {
  // console.log(label,'Value in WrappedControl:', value);

  const isChecked = typeof(unlimitedValue) !== "undefined" && typeof(defaultValue) !== "undefined" && unlimitedValue !== defaultValue && value === unlimitedValue;
  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    // console.log('handling it',checked,event.target.checked)
    if (checked) {
    } else {
      // console.log(defaultValue||0)
      if (restProps.updateAttribute) {
        // console.log(defaultValue||0)
        restProps.updateAttribute(defaultValue||0);
      }
    }
};

  // console.log(value, unlimitedValue,typeof(value), typeof(unlimitedValue))

  if (isChecked) {
    return (
      <Tooltip title="No limit">
        <>
        <Typography>
          <Text underline style={restProps.labelStyle}>
            {label}
          </Text>
        </Typography>        
        <FormControlLabel 
          control={
            <Switch 
              checked={isChecked}
              onChange={handleSwitchChange}
              size={restProps.size}
              disabled={restProps.disabled}
            />
            }
              label="No limit"
          />
          </>
      </Tooltip>
    );
  }

  return ttt ? (
    <Tooltip title={ttt}>{children}</Tooltip>
  ) : children;
};



interface PreferenceChildObjectProps extends PreferenceProps {
  label?: React.ReactNode;
  labelVariant?: TypographyTypeMap["props"]["variant"]  
  children?: React.ReactNode; 
}

export const PreferenceChildObject: React.FC<PreferenceChildObjectProps> = ({ 
  label, 
  labelVariant, 
  ttt,
  children
  }) => {
    
  let labelStyle = getLabelStyle({disabled:false,labelVariant});
  
  let actualComponent =
    (<Box style={{width:"100%"}}>
      <Typography >
        {/* variant={labelVariant || "caption"}  */}
        <Text underline style={labelStyle} >
          {label}
        </Text>
      </Typography>
      {children}
      </Box>)
    
  // If ttt is truthy, wrap the switch component with Tooltip
  return ttt ? (
    <Tooltip title={ttt}>
      {actualComponent}
    </Tooltip>
  ) : actualComponent;  
};


